import React from "react";
import { grainFrames } from "./grain";
import { TEXTURES } from "./specs";
import type { ThemeId } from "../themes/types";

/** Tile swaps per second. Real emulsion resamples at 24; this is enough to
 *  read as alive without repainting a full-viewport blended layer that often. */
const FPS = 12;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The grain layer for the active theme.
 *
 * Cycling happens by writing `backgroundImage` straight to the node on an
 * interval rather than through state. At 12fps a `setState` here would be
 * harmless on its own, but this layer sits above every theme, and per-frame
 * state on shared layers is exactly what froze the renderer before. Writing to
 * the node keeps React out of the loop entirely.
 *
 * Note the layer is deliberately *not* transformed — it blends, and animating
 * a transform on a blended full-viewport element re-composites the whole
 * viewport every step. The gate weave moves the content underneath instead.
 */
const FilmGrain: React.FC<{ themeId: ThemeId }> = ({ themeId }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const spec = TEXTURES[themeId];

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const frames = grainFrames(themeId, spec.grain);
    if (!frames.length) return;

    /*
     * Re-phase on every swap.
     *
     * A repeating tile is periodic no matter how large it is, and the eye is
     * very good at locking onto that: at a 96px tile the grain stopped reading
     * as grain and started reading as woven mesh, a visible grid 15 cells
     * across. Bigger tiles alone only reduce the count. Shifting the origin
     * each frame changes the phase constantly, so there is no stable pattern
     * left to lock onto — and since the tile itself changes at the same
     * moment, the shift is indistinguishable from fresh grain.
     */
    const rephase = () => {
      const s = spec.grain.size;
      el.style.backgroundPosition = `${Math.floor(Math.random() * s)}px ${Math.floor(
        Math.random() * s
      )}px`;
    };

    el.style.backgroundImage = `url(${frames[0]})`;
    rephase();
    if (frames.length === 1 || prefersReducedMotion()) return;

    let i = 0;
    const timer = window.setInterval(() => {
      i = (i + 1) % frames.length;
      el.style.backgroundImage = `url(${frames[i]})`;
      rephase();
    }, 1000 / FPS);

    return () => window.clearInterval(timer);
  }, [themeId, spec.grain]);

  return (
    <div
      ref={ref}
      className="film-grain"
      aria-hidden="true"
      style={{
        opacity: spec.grainOpacity,
        backgroundSize: `${spec.grain.size}px ${spec.grain.size}px`,
      }}
    />
  );
};

export default FilmGrain;
